import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import useUpdateMenu from "./hooks/useUpdateMenu";
import useDeleteMenu from "./hooks/useDeleteMenu";
import { useMenuContext } from "store/contexts/Menus";
import AppModal from "components/ui/AppModal";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton";
import AppDropDown from "components/ui/AppDropdown";
import AppIcon from "components/ui/AppIcon"
import AppSwitch from "components/ui/AppSwitch";
import AppTag from "components/ui/AppTag";

const MenuListing = ({ as="page", menus, setOpen, CreateNewMenu, getMenuById, setMenuId, setCurrentMode }) => {
  const router = useHistory();
  const currentPage = router?.location?.state?.activePage ?? 1;
  const recordsPerPage = router?.location?.state?.pageSize ?? 50;
  const { t } = useTranslation();
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const [mode,setMode] = useState("list"); // create, edit, delete

  const updateMenu = useUpdateMenu();
  const deleteMenu = useDeleteMenu();
  const { resetState, updateState } = useMenuContext();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    item: null,
  });
  const [searchTerm, setSearch] = useState(null);
  const getMenu = async (id, cb) => {
    resetState();
    await getMenuById(id);
    updateState(cb, true);
  };
  const items = [
    { 
      label:(<span className="flex items-center text-gray-600 gap-3 px-2 py-2"><AppIcon name="edit" />Edit</span>), 
      key: 'edit',
    }, 
    { label:(<span className="flex items-center text-gray-600 gap-3 px-2 py-2"><AppIcon name="copy" />Duplicate</span>), 
      key: 'duplicate',
    },
    { label:(<span className="flex items-center text-gray-600 gap-3 px-2 py-2"><AppIcon name="trash" />Delete</span>), 
      key: 'delete',
    },
  ];

  const handleActionClick = (key, rowData) => {
    if (!rowData?._id) return;

    if (key === "edit") {
      getMenu(rowData._id, "editMenumodal");
      return;
    }

    if (key === "duplicate") {
      getMenu(rowData._id, "cloneMenuModal");
      return;
    }

    if (key === "delete") {
      onClickDeleteButton(rowData);
    }
  };

  const Actions = ({ rowData }) => {
    return (
      <AppDropDown
        icon="more"
        dropdownMenu={items}
        onItemClick={(key) => handleActionClick(key, rowData)}
      />
    );
  };

  const columns = [
    {
      title: t("Name"),
      dataIndex: "baseLanguage",
      align: "initial",
      key:"baseLanguage",
      render: (value, rowData, index) => {
        return (
          <div className="flex gap-2 default-tag">
            <span
              className="underline cursor-pointer text-blue-500"
              onClick={() => {
                resetState();
                if(as === "page") {
                  router.push({
                    pathname: `/app/dashboard/menu-editor/${rowData?._id}`,
                    state: {
                      menu: rowData,
                    },
                  });
                } else {
                  setMenuId(rowData?._id);
                  setCurrentMode("edit");
                }
              }}
            >
              {value?.name}
            </span>

            {rowData?.isDefault ? (
              <AppTag variant="success" size="sm">{t("Default")}</AppTag>
            ) : (
              ""
            )}
          </div>
        );
      },
    },
    {
      title: t("Used By"),
      dataIndex: "stores",
      align: "initial",
      key:"stores",
      render: (value, rowData, index) => {
        return (
          <span
            onClick={() => {
              setOpen(true, rowData?._id);
            }}
            className="underline cursor-pointer text-blue-500 "
          >
            {`${value?.length} ${t("Outlet(s)")}`}
          </span>
        );
      },
    },
    {
      title: t("Last Updated"),
      dataIndex: "updatedAt",
      align: "initial",
      key:"updatedAt",
      render: (value, rowData, index) => {
        return <>{moment(value).format("DD MMM YYYY")}</>;
      },
    },
    {
      title: "Enable/Disable",
      dataIndex: "isActive",
      key:"isActive",
      align: "initial",
      render: (value, rowData, index) => {
        const isActive = rowData.isActive;
        return (
          <AppSwitch
            checked={isActive}
            disabled={rowData?.isDefault || isLoading}
            onChange={(checked) => {
              updateMenu({ rowData, event: checked });
            }}
          />
        );
      },
    },
    {
      title: t("Action"),
      dataIndex: "action",
      key:"action",
      align: "center",
      fixed: "right",
      render: (value, rowData, index) => {
        return <Actions rowData={rowData} />;
      },
    },
  ];

  const FilteredData = useMemo(() => {
    let searchTermLower = searchTerm?.toLowerCase()?.trim();

    if (searchTerm === "" || searchTerm === null) {
      return menus;
    }
    return menus?.filter((group) =>
      group.baseLanguage?.name?.toLowerCase()?.includes(searchTermLower)
    );
  }, [menus, searchTerm]);

  const totalPages = Math.max(1, Math.ceil((FilteredData?.length || 0) / recordsPerPage));
  const effectivePage = Math.min(Math.max(currentPage, 1), totalPages);

  const handleSearch = (value) => {
    router.push(router.location.pathname, {
      ...router?.location?.state,
      pageSize: recordsPerPage,
      activePage: 1,
    });
    setSearch(value?.toLowerCase() ?? "");
  };

  const onPaginationChange = (newPage, newPageSize) => {
    const nextPageSize = newPageSize ?? recordsPerPage;
    const updates = {};
    if (recordsPerPage !== nextPageSize) {
      updates.pageSize = nextPageSize;
    }
    if (currentPage !== newPage) {
      updates.activePage = newPage;
    }
    if (Object.keys(updates).length > 0) {
      router.push(router.location.pathname, {
        ...router?.location?.state,
        ...updates,
      });
    }
  };

  const onClickDeleteButton = (rowData) => {
    if (rowData?._id) {
      setDeleteModal({ isOpen: true, item: rowData });
    }
  };

  const handleCancel = () => {
    setDeleteModal({ isOpen: false, item: null });
  };

  const handleDelete = async () => {
    if (!deleteModal.item?._id) return;
    await deleteMenu({ menuId: deleteModal.item?._id });
    handleCancel();
  };

  const deleteFooter = (
    <div className="mx-auto flex w-full max-w-[760px] items-center justify-center gap-4 pb-2">
      <AppButton
        variant="secondary"
        size="lg"
        className="w-1/2 border border-gray-400 bg-white text-4xl text-gray-500"
        disabled={isLoading}
        onClick={handleCancel}
      >
        {t("No, Keep It")}
      </AppButton>
      <AppButton
        variant="primary"
        size="lg"
        className="w-1/2 text-4xl"
        loading={isLoading}
        disabled={isLoading}
        onClick={handleDelete}
      >
        {t("Yes, Delete")}
      </AppButton>
    </div>
  );

  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between justify-center md:items-center">
        <h2 className="text-20px font-semi-bold">{t("Menus")}</h2>
        <div className="flex flex-col md:flex-row md:justify-end justify-center md:items-center gap-3 my-3 ">
          <AppSearchInput
            placeholder={t("Search by name")}
            value={searchTerm ?? ""}
            onInput={(event) => handleSearch(event.target.value)}
          />
          <AppButton onClick={CreateNewMenu}>{t("Create Menu")}</AppButton>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <AppPaginatedTable
          columns={columns}
          data={FilteredData}
          clientSidePagination
          page={effectivePage}
          pageSize={recordsPerPage}
          onPageChange={onPaginationChange}
          rowOptions={[10, 20, 50, 100]}
        />
      </div>

      <AppModal
        open={deleteModal.isOpen}
        onClose={handleCancel}
        width={550}
        closable={false}
        footer={deleteFooter}
      >
        <div className="flex flex-col items-center px-8 text-center">
          <div className="mb-6 mt-6 h-44 w-44">
            <AppIcon name="trashRed" size={170} />
          </div>
          <h1 className="text-24px font-semibold text-gray-900">{t("Delete Menu")}</h1>
          <p className="mb-0 text-3xl text-gray-500">
            {deleteModal.item?.baseLanguage?.name
              ? t("Are you sure you want to delete {{name}}?", {
                  name: deleteModal.item.baseLanguage.name,
                })
              : t("Are you sure you want to delete this Menu")}
          </p>
        </div>
      </AppModal>
    </>
  );
};

export default MenuListing;
MenuListing.propTypes = {
  item: PropTypes.object,
};
